import React, { useEffect, useRef, useState } from 'react';
import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';

const DEFAULT_ERROR_TOAST = 'Error';
const DEFAULT_SUCCESS_DISPLAY_MS = 150;
const DEFAULT_ERROR_DISPLAY_MS = 1200;

type AsyncActionButtonVisualState = 'idle' | 'pending' | 'success' | 'error';

export class AsyncActionButtonAbortError extends Error {
  constructor() {
    super('ASYNC_ACTION_BUTTON_ABORT');
    this.name = 'AsyncActionButtonAbortError';
  }
}

export type AsyncActionButtonProps = Omit<
  LoadingButtonProps,
  'children' | 'color' | 'loading' | 'onClick'
> & {
  debugId?: string;
  styleVariant: 'green' | 'red';
  idleLabel: string;
  pendingLabel: string;
  successLabel: string;
  errorLabel?: string;
  onAction: () => Promise<void>;
  onAfterSuccessDisplay?: () => void | Promise<void>;
  successDisplayMs?: number;
  successToast?: string;
  errorToast?: string;
  onAfterCycleComplete?: () => void;
};

const waitFor = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const AsyncActionButton: React.FC<AsyncActionButtonProps> = ({
  debugId,
  styleVariant,
  idleLabel,
  pendingLabel,
  successLabel,
  errorLabel,
  onAction,
  onAfterSuccessDisplay,
  successDisplayMs = DEFAULT_SUCCESS_DISPLAY_MS,
  successToast,
  errorToast,
  onAfterCycleComplete,
  disabled = false,
  variant = 'contained',
  ...buttonProps
}) => {
  const [visualState, setVisualState] = useState<AsyncActionButtonVisualState>('idle');
  const mountedRef = useRef(true);
  const activeActionIdRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const color = styleVariant === 'green' ? 'primary' : 'secondary';
  const isPending = visualState === 'pending';
  const debugLog = (message: string, details?: unknown) => {
    if (!debugId) {
      return;
    }

    if (details === undefined) {
      console.log(`[${debugId}] ${message}`);
      return;
    }

    console.log(`[${debugId}] ${message}`, details);
  };

  const label =
    visualState === 'pending'
      ? pendingLabel
      : visualState === 'success'
      ? successLabel
      : visualState === 'error' && errorLabel
      ? errorLabel
      : idleLabel;

  const handleClick = async () => {
    if (disabled || inFlightRef.current) {
      debugLog('click ignored', { disabled, inFlight: inFlightRef.current, visualState });
      return;
    }

    inFlightRef.current = true;
    const actionId = activeActionIdRef.current + 1;
    activeActionIdRef.current = actionId;
    debugLog('click accepted', { actionId, visualStateBefore: visualState });
    setVisualState('pending');
    debugLog('visual state set to pending', { actionId });

    try {
      debugLog('calling onAction', { actionId });
      await onAction();
      debugLog('onAction resolved', { actionId });

      if (!mountedRef.current || activeActionIdRef.current !== actionId) {
        debugLog('stopping after onAction because component is stale', {
          actionId,
          mounted: mountedRef.current,
          activeActionId: activeActionIdRef.current,
        });
        return;
      }

      setVisualState('success');
      debugLog('visual state set to success', { actionId });

      if (successToast) {
        debugLog('showing success toast', { actionId, successToast });
        toast.success(successToast);
      }

      if (successDisplayMs > 0) {
        debugLog('waiting for success display', { actionId, successDisplayMs });
        await waitFor(successDisplayMs);
        debugLog('success display wait finished', { actionId });
      }

      if (!mountedRef.current || activeActionIdRef.current !== actionId) {
        debugLog('stopping after success wait because component is stale', {
          actionId,
          mounted: mountedRef.current,
          activeActionId: activeActionIdRef.current,
        });
        return;
      }

      if (onAfterSuccessDisplay) {
        debugLog('calling onAfterSuccessDisplay', { actionId });
        await onAfterSuccessDisplay();
        debugLog('onAfterSuccessDisplay resolved', { actionId });
      }

      if (!mountedRef.current || activeActionIdRef.current !== actionId) {
        debugLog('stopping after onAfterSuccessDisplay because component is stale', {
          actionId,
          mounted: mountedRef.current,
          activeActionId: activeActionIdRef.current,
        });
        return;
      }

      inFlightRef.current = false;
      setVisualState('idle');
      debugLog('visual state set to idle after success cycle', { actionId });
      onAfterCycleComplete?.();
    } catch (error) {
      debugLog('caught error', {
        actionId,
        name: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
      });
      if (!mountedRef.current || activeActionIdRef.current !== actionId) {
        debugLog('stopping inside catch because component is stale', {
          actionId,
          mounted: mountedRef.current,
          activeActionId: activeActionIdRef.current,
        });
        return;
      }

      if (error instanceof AsyncActionButtonAbortError) {
        inFlightRef.current = false;
        setVisualState('idle');
        debugLog('abort error reset button to idle', { actionId });
        onAfterCycleComplete?.();
        return;
      }

      if (errorLabel) {
        setVisualState('error');
      } else {
        setVisualState('idle');
      }
      debugLog('showing error toast', { actionId, errorToast: errorToast ?? DEFAULT_ERROR_TOAST });

      toast.error(errorToast ?? DEFAULT_ERROR_TOAST);

      if (errorLabel) {
        debugLog('waiting for error display', { actionId, errorDisplayMs: DEFAULT_ERROR_DISPLAY_MS });
        await waitFor(DEFAULT_ERROR_DISPLAY_MS);

        if (!mountedRef.current || activeActionIdRef.current !== actionId) {
          debugLog('stopping after error wait because component is stale', {
            actionId,
            mounted: mountedRef.current,
            activeActionId: activeActionIdRef.current,
          });
          return;
        }

        setVisualState('idle');
        debugLog('visual state set to idle after error display', { actionId });
      }

      inFlightRef.current = false;
      debugLog('error cycle complete', { actionId });
      onAfterCycleComplete?.();
    }
  };

  return (
    <LoadingButton
      {...buttonProps}
      color={color}
      data-style-variant={styleVariant}
      disabled={disabled || isPending}
      loading={isPending}
      onClick={() => {
        void handleClick();
      }}
      // REQ-011: async actions should use the same shared quiet variant as plain site buttons.
      variant={variant}
    >
      {label}
    </LoadingButton>
  );
};

export default AsyncActionButton;
